import { Autocomplete, Chip, TextField, TextFieldProps } from '@mui/material';
import React, { useState } from 'react';

export type SimpleChipsInputProps = Omit<TextFieldProps, 'onChange'> & {
  value: string[];
  options?: string[];
  separator?: string | string[];
  validateOption?: (option: string) => boolean;
  /** When true, input is not editable but not visually disabled (readonly). */
  readOnly?: boolean;
  onChange: (event: React.SyntheticEvent, value: string[]) => void;
};

export function SimpleChipsInput({
  value,
  options,
  separator = [',', ' '],
  onChange,
  onBlur,
  validateOption,
  readOnly,
  ...rest
}: SimpleChipsInputProps) {
  const separators = Array.isArray(separator) ? separator : [separator];
  const [inputValue, setInputValue] = useState('');

  const parseItems = (value: string) => {
    let result = [value];

    // Split by each separator in sequence
    separators.forEach((sep) => {
      result = result.flatMap((item) => item?.split(sep) ?? []);
    });

    return result.map((s) => s?.trim()).filter(Boolean);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      selectOnFocus
      disableClearable
      clearOnBlur
      options={options || []}
      value={value}
      inputValue={inputValue}
      disabled={rest.disabled}
      readOnly={readOnly}
      open={readOnly ? false : undefined}
      onChange={(event, value) => {
        if (validateOption) {
          value = (value as string[]).filter((item: string) => validateOption(item));
        }

        onChange(event, value as string[]);
      }}
      onInputChange={(event, newInputValue) => {
        const newOptionCreated = separators.some(
          (sep) => newInputValue.endsWith(sep) && !!newInputValue.slice(0, -sep.length).trim(),
        );

        if (newOptionCreated && (!validateOption || validateOption(newInputValue))) {
          onChange(event, [...(value || []), ...parseItems(newInputValue)]);
        } else {
          setInputValue(newInputValue);
        }
      }}
      renderTags={(value, getTagProps) =>
        (value as string[]).map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });

          return <Chip key={key} label={option} {...tagProps} />;
        })
      }
      renderOption={(props, option) => {
        return (
          <li {...props} key={option as string}>
            {option as React.ReactNode}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...rest}
          placeholder={!value.length ? rest.placeholder : undefined}
          variant={rest.variant ?? 'outlined'}
          onBlur={(event) => {
            const items = parseItems(event.target.value);

            if (items.length) {
              onChange(event, [...(value || []), ...items]);
            }

            onBlur?.(event);
          }}
          inputProps={{
            ...params.inputProps,
            readOnly: readOnly ?? false,
          }}
        />
      )}
    />
  );
}
